import {
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { EquipmentOwner } from '../types/equipmentOwner';
import { addAuthHeaders } from '../api';
import { config } from '../config/rental';
import { ApproveEquipmentOrder } from '../types/api/approveEquipmentOrder';
import { MoveEquipmentAssignment } from '../types/api/moveEquipmentAssignment';
import { MoveEquipmentOrder } from '../types/api/moveEquipmentOrder';
import {
  NewEquipmentChangeOrder,
  NewEquipmentOrder,
} from '../types/api/newEquipmentOrder';
import { Roles } from '../types/enums/rentalRoles';
import { Equipment } from '../types/equipment';
import { EquipmentCategory } from '../types/equipmentCategory';
import { EquipmentProject } from '../types/equipmentProject';
import { Organization } from '../types/organization';
import { DateStr, DateTime } from '../types/util/dateTime';
import { EquipmentAssignment } from '../types/equipmentAssignment';
import { EquipmentUnavailability } from '../types/equipmentUnavailability';
import { Guid } from '../types/guid';
import { NewUnavailability } from '../types/api/newUnavailability';
import { CalendarObservation } from '../types/calendarObservation';
import { Worker } from '../types/worker';
import { EquipmentOrder } from '../types/equipmentOrder';
import { DeliverEquipmentAssignment } from '../types/api/DeliverEquipmentAssignment';
import { LinkRedirect } from '../types/linkRedirect';
import { Company } from '../types/company';
import { convertEquipmentAssignment2MoveEquipmentAssignment } from '../logic/typeConverters';
import { ChangeProjectRequest } from '../types/api/changeProject';

// Define a service using a base URL and expected endpoints
export const rentalApi = createApi({
  reducerPath: 'rental',
  baseQuery: fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (h) => addAuthHeaders(config, h), // Adds msal auth headers
  }),
  tagTypes: [
    'projects',
    'equipment',
    'assignments',
    'unavailabilities',
    'workers',
    'myCompany',
    'categories',
  ],
  endpoints: (builder) => ({
    getCalendar: builder.query<
      CalendarObservation[],
      { from?: string; to?: string } | void
    >({
      query: (data) => {
        const from = data?.from;
        const to = data?.to;
        return {
          url: '/calendar',
          params: {
            from,
            to,
          },
        };
      },
      providesTags: ['myCompany'],
    }),
    getOrder: builder.query<EquipmentOrder, string>({
      query: (id) => ({
        url: `/orders/${id}`,
      }),
      providesTags: ['assignments', 'myCompany'],
    }),
    getUserOrders: builder.query<EquipmentOrder[], DateTime>({
      query: (from) => ({
        url: '/orders/me',
        params: { from },
      }),
      providesTags: ['assignments', 'myCompany'],
    }),
    getOrders: builder.query<
      EquipmentOrder[],
      { from: DateTime; userOnly?: boolean }
    >({
      query: ({ from, userOnly }) => ({
        url: `/orders${userOnly ? '/me' : ''}`,
        params: { from },
      }),
      providesTags: ['assignments', 'myCompany'],
    }),
    putApproveOrderBody: builder.mutation<
      EquipmentAssignment,
      { id: string; body: ApproveEquipmentOrder }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}/assign`,
        method: 'PUT',
        body,
      }),
      // invalidates assignments tag because this endpoint can change assignments
      invalidatesTags: ['assignments'],
    }),
    postEquipmentOrder: builder.mutation<
      EquipmentAssignment,
      NewEquipmentOrder | NewEquipmentChangeOrder
    >({
      query: (body) => ({
        url: '/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    putMoveEquipmentOrder: builder.mutation<
      EquipmentAssignment,
      { id: string; body: MoveEquipmentOrder }
    >({
      query: ({ id, body }) => ({
        url: `/orders/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    deleteOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `/orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['assignments'],
    }),
    getProject: builder.query<EquipmentProject, number>({
      query: (id) => `/projects/${id}`,
      providesTags: ['projects', 'myCompany'],
    }),
    getAllProjects: builder.query<EquipmentProject[], void | { q?: string }>({
      query: (q) => ({
        url: '/projects',
        params: {
          q,
        },
      }),
      providesTags: ['projects', 'myCompany'],
    }),
    getCategories: builder.query<
      EquipmentCategory[],
      void | { includeSubCategories?: boolean; owners?: number[] }
    >({
      query: (
        { includeSubCategories, owners } = {
          includeSubCategories: false,
          owners: undefined,
        },
      ) => ({
        url: '/categories',
        params: {
          includeSubCategories,
          owners,
        },
      }),
      providesTags: ['categories', 'myCompany'],
    }),
    getAllEquipment: builder.query<Equipment[], void | { owners?: number[] }>({
      query: ({ owners = [] } = { owners: [] }) => ({
        url: '/equipment',
        params: {
          owners: owners.join(',') || undefined,
        },
      }),
      providesTags: ['equipment', 'myCompany'],
    }),
    getExtraEquipment: builder.query<Equipment[], number | string>({
      query: (id) => `/equipment/${id}/extra-equipment`,
      providesTags: ['equipment', 'myCompany'],
    }),
    getEquipment: builder.query<Equipment, string|number>({
      query: (id) => `/equipment/${id}`,
      providesTags: ['equipment', 'myCompany'],
    }),
    getEquipmentAssignmentsFor: builder.query<
      EquipmentAssignment[],
      { id: number | string; from?: DateTime; to?: DateTime }
    >({
      query: ({ id, from, to }) => ({
        url: `/equipment/${id}/assignments`,
        params: { from, to },
      }),
      providesTags: ['assignments', 'myCompany'],
    }),
    getEquipmentOwners: builder.query<EquipmentOwner[], void>({
      query: () => '/equipment/owners',
      providesTags: ['myCompany'],
    }),
    putEquipmentComment: builder.mutation<
      Equipment,
      { id: string | number; comment: string }
    >({
      query: ({ id, comment }) => ({
        url: `/equipment/${id}/comment`,
        method: 'PUT',
        params: {
          comment,
        },
      }),
      invalidatesTags: ['equipment'],
    }),
    getEquipmentAssignment: builder.query<EquipmentAssignment, string>({
      query: (id) => `/assignments/${id}`,
      providesTags: ['assignments', 'myCompany'],
    }),
    getUserAssignments: builder.query<EquipmentAssignment[], DateTime>({
      query: (from) => ({
        url: '/assignments/me',
        params: { from },
      }),
      providesTags: ['assignments', 'myCompany'],
    }),
    getAssignments: builder.query<
      EquipmentAssignment[],
      { from?: DateTime; projectId?: number | string }
    >({
      query: ({ from, projectId }) => ({
        url: projectId
          ? `/assignments/project/${projectId}`
          : '/assignments/me',
        params: { from },
      }),
      providesTags: ['assignments', 'myCompany'],
    }),
    getAllEquipmentAssignments: builder.query<
      EquipmentAssignment[],
      {
        organizations?: number[];
        equipmentOwners?: number[];
        from: DateStr;
        to: DateStr;
        filterOrders?: boolean;
      }
    >({
      query: ({
        organizations = [],
        equipmentOwners = [],
        from,
        to,
        filterOrders,
      }) => ({
        url: '/assignments',
        params: {
          organizations: organizations.join(',') || undefined,
          equipmentOwners: equipmentOwners.join(',') || undefined,
          from,
          to,
          filterOrders,
        },
      }),
      providesTags: ['assignments', 'myCompany'],
    }),
    putApproveAssignmentBody: builder.mutation<
      EquipmentAssignment,
      { id: string; body: MoveEquipmentAssignment }
    >({
      query: ({ id, body }) => ({
        url: `/assignments/${id}/approve`,
        method: 'PUT',
        body,
      }),
      // invalidates assignments tag because this endpoint can change assignments
      invalidatesTags: ['assignments', 'equipment'],
    }),
    putDeliverAssignmentBody: builder.mutation<
      EquipmentAssignment,
      { id: string; body: DeliverEquipmentAssignment }
    >({
      query: ({ id, body }) => ({
        url: `/assignments/${id}/deliver`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    putMoveEquipmentAssignment: builder.mutation<
      EquipmentAssignment,
      { id: string; body: MoveEquipmentAssignment }
    >({
      query: ({ id, body }) => ({
        url: `/assignments/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    deleteAssignment: builder.mutation<void, string>({
      query: (id) => ({
        url: `/assignments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['assignments'],
    }),
    postCopyAssignment: builder.mutation<EquipmentAssignment, string>({
      query: (id) => ({
        url: `/assignments/${id}/copy`,
        method: 'POST',
      }),
      invalidatesTags: ['assignments'],
    }),
    postCreateAssignment: builder.mutation<
      EquipmentAssignment,
      MoveEquipmentAssignment
    >({
      query: (body) => ({
        url: '/assignments',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    postCreateAndApproveAssignment: builder.mutation<
      EquipmentAssignment,
      MoveEquipmentAssignment
    >({
      async queryFn(body, _queryApi, _extraOptions, baseQueryFetch) {
        // Create new assignment
        const createdData = await baseQueryFetch({
          url: '/assignments',
          method: 'POST',
          body,
        });

        // If the request failed
        if (createdData.error) {
          return { error: createdData.error as FetchBaseQueryError };
        }

        // Get the ID
        const assignmentId = (createdData.data as EquipmentAssignment)?.id;
        if (!assignmentId) {
          return {
            error: {
              status: 404,
              data: 'Assignment ID not found',
            } as FetchBaseQueryError,
          };
        }

        // Convert the object
        const obj = convertEquipmentAssignment2MoveEquipmentAssignment(
          createdData.data as EquipmentAssignment,
        );

        // Approve the assignment
        const result = await baseQueryFetch({
          url: `/assignments/${assignmentId}/approve`,
          method: 'PUT',
          body: obj,
        });

        return result.data
          ? { data: result.data as EquipmentAssignment }
          : { error: result.error as FetchBaseQueryError };
      },
      invalidatesTags: ['assignments', 'equipment'],
    }),
    getOrganizations: builder.query<Organization[], void>({
      query: () => '/organizations',
      providesTags: ['myCompany'],
    }),
    getMyOrganization: builder.query<Organization, void>({
      query: () => '/organizations/me',
      providesTags: ['myCompany'],
    }),
    getMyRoles: builder.query<Roles[], void>({
      query: () => '/organizations/me/roles',
      providesTags: ['myCompany'],
    }),
    getMyCompany: builder.query<Company | undefined, void>({
      async queryFn(_arg, _queryApi, _extraOptions, baseQueryFetch) {
        const result = await baseQueryFetch({
          url: '/me/company',
        });

        const error = result.error as any;
        if (error?.status === 404 || error?.originalStatus === 404) {
          return { data: undefined };
        }

        return result.data
          ? { data: result.data as Company }
          : { error: result.error as FetchBaseQueryError };
      },
      providesTags: ['myCompany'],
    }),
    setMyCompany: builder.mutation<string, string>({
      query: (companyName) => ({
        url: '/me/company',
        method: 'PUT',
        body: { companyName },
        headers: { 'Content-Type': 'application/json' },
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
              `Error ${response.status}: ${errorText || response.statusText}`,
            );
          }
          return response.text();
        },
      }),
      invalidatesTags: ['myCompany'],
    }),
    getEquipmentUnavailability: builder.query<
      EquipmentUnavailability,
      { equipmentId: string; id: Guid }
    >({
      query: ({ equipmentId, id }) => ({
        url: `/equipment/${equipmentId}/unavailabilities/${id}`,
      }),
      providesTags: ['unavailabilities', 'myCompany'],
    }),
    getUnavailabilities: builder.query<
      EquipmentUnavailability[],
      {
        id: string;
        start?: string | undefined;
        end?: string | undefined;
      }
    >({
      query: ({ id, start, end }) => ({
        url: `/equipment/${id}/unavailabilities`,
        params: {
          start,
          end,
        },
      }),
      providesTags: ['unavailabilities', 'myCompany'],
    }),
    getSearchUnavailabilities: builder.query<
      EquipmentUnavailability[],
      {
        id: string;
        q: string;
        limit?: number;
      }
    >({
      query: ({ id, q, limit }) => ({
        url: `/equipment/${id}/unavailabilities/search`,
        params: {
          q,
          limit,
        },
      }),
      providesTags: ['unavailabilities', 'myCompany'],
    }),
    putEquipmentUnavailability: builder.mutation<
      EquipmentUnavailability,
      {
        equipmentId: string;
        id: Guid;
        body: NewUnavailability;
      }
    >({
      query: ({ equipmentId, id, body }) => ({
        url: `/equipment/${equipmentId}/unavailabilities/${id}`,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['unavailabilities'],
    }),
    postEquipmentUnavailability: builder.mutation<
      void,
      { id: string; body: NewUnavailability }
    >({
      query: ({ id, body }) => ({
        url: `/equipment/${id}/unavailabilities`,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['unavailabilities'],
    }),
    deleteEquipmentUnavailability: builder.mutation<
      EquipmentUnavailability,
      { equipmentId: string; id: Guid }
    >({
      query: ({ equipmentId, id }) => ({
        url: `/equipment/${equipmentId}/unavailabilities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['unavailabilities'],
    }),

    getAllUnavailabilities: builder.query<
      EquipmentUnavailability[],
      { start?: DateTime; end?: DateTime } | void
    >({
      query: ({ start, end } = {}) => ({
        url: '/equipment/unavailabilities',
        params: {
          start,
          end,
        },
      }),
      providesTags: ['unavailabilities', 'myCompany'],
    }),
    postEquipmentPosition: builder.mutation<
      void,
      { id: string; projectId?: number }
    >({
      query: ({ id, projectId }) => ({
        url: `/equipment/${id}/position`,
        method: 'POST',
        body: projectId || 0,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['equipment'],
    }),
    getAllWorkers: builder.query<
      Worker[],
      void | { q?: string; email?: string; activeOnly?: boolean }
    >({
      query: ({ q, email, activeOnly } = { activeOnly: true }) => ({
        url: '/workers',
        params: {
          q,
          email,
          activeOnly,
        },
      }),
      providesTags: ['workers', 'myCompany'],
      // To do local testing with a user object:
      // transformResponse: (rv: Worker[]) => (
      //   rv.map((w) => (
      //     w.email === 'krios@tsmaskin.no'
      //       ? { ...w, email: 'krte@tsmaskin.no' }
      //       : w
      //   ))
      // ),
    }),
    getRedirectLink: builder.query<LinkRedirect, number>({
      query: (id) => `/link/${id}`,
    }),
    changeProjects: builder.mutation<
      EquipmentAssignment,
      { id: string; body: ChangeProjectRequest }
    >({
      query: ({ id, body }) => ({
        url: `/assignments/${id}/change-project`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['assignments'],
    }),
    revertProjectChange: builder.mutation<
      void,
      {
        assignmentToDeleteId: string;
        assignmentToRevert: { id: string, body: MoveEquipmentAssignment };
      }
    >({
      async queryFn(args, _queryApi, _extraOptions, baseQuery) {
        const [deleteRes, revertRes] = await Promise.all([
          baseQuery({
            url: `/assignments/${args.assignmentToDeleteId}`,
            method: 'DELETE',
          }),
          baseQuery({
            url: `/assignments/${args.assignmentToRevert.id}`,
            method: 'PUT',
            body: args.assignmentToRevert.body,
          }),
        ]);

        if (deleteRes.error) return { error: deleteRes.error };
        if (revertRes.error) return { error: revertRes.error };

        return { data: undefined };
      },
      invalidatesTags: ['assignments'],
    }),
    getCompanyList: builder.query<string[], void>({
      query: () => '/companies',
      providesTags: ['myCompany'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // Sorted ascending
  useDeleteAssignmentMutation,
  useDeleteEquipmentUnavailabilityMutation,
  useDeleteOrderMutation,
  useGetAllEquipmentAssignmentsQuery,
  useGetAllEquipmentQuery,
  useGetAllProjectsQuery,
  useGetAllUnavailabilitiesQuery,
  useGetAllWorkersQuery,
  useGetCalendarQuery,
  useGetCategoriesQuery,
  useGetEquipmentAssignmentQuery,
  useGetEquipmentOwnersQuery,
  useGetEquipmentQuery,
  useGetEquipmentAssignmentsForQuery,
  useGetEquipmentUnavailabilityQuery,
  useGetExtraEquipmentQuery,
  useGetMyOrganizationQuery,
  useGetMyRolesQuery,
  useGetMyCompanyQuery,
  useSetMyCompanyMutation,
  useGetOrderQuery,
  useGetOrganizationsQuery,
  useGetProjectQuery,
  useGetSearchUnavailabilitiesQuery,
  useGetUnavailabilitiesQuery,
  useGetUserAssignmentsQuery,
  useGetAssignmentsQuery,
  useGetUserOrdersQuery,
  useGetOrdersQuery,
  usePostCopyAssignmentMutation,
  usePostCreateAssignmentMutation,
  usePostEquipmentOrderMutation,
  usePostEquipmentPositionMutation,
  usePostEquipmentUnavailabilityMutation,
  usePutApproveAssignmentBodyMutation,
  usePutApproveOrderBodyMutation,
  usePostCreateAndApproveAssignmentMutation,
  usePutDeliverAssignmentBodyMutation,
  usePutEquipmentCommentMutation,
  usePutEquipmentUnavailabilityMutation,
  usePutMoveEquipmentAssignmentMutation,
  usePutMoveEquipmentOrderMutation,
  useGetRedirectLinkQuery,
  useChangeProjectsMutation,
  useRevertProjectChangeMutation,
  useGetCompanyListQuery,
  util,
} = rentalApi;

export default rentalApi.reducer;
